import React from 'react';
import PropTypes from 'prop-types';
import './systemRecommendation.scss';

// Derives an ADVISORY suggestion from the two engines. This never decides —
// it summarises what the rule engine and risk analysis found, so the
// underwriter has a starting point. The underwriter always decides.
const deriveRecommendation = (assessment, risk, engineMode) => {
  const ruleVerdict = assessment?.verdict; // ELIGIBLE | BORDERLINE | NOT_ELIGIBLE
  const riskBand = risk?.band; // LOW | MODERATE | HIGH | UNKNOWN

  // Rule-only mode: base it on the rule verdict alone.
  if (engineMode === 'rule') {
    if (!ruleVerdict) return null;
    if (ruleVerdict === 'ELIGIBLE')
      return {
        tone: 'approve',
        text: 'Meets all rule criteria',
        detail: 'Rule engine: eligible. No policy violations found.'
      };
    if (ruleVerdict === 'BORDERLINE')
      return {
        tone: 'review',
        text: 'Borderline — review needed',
        detail: 'Rule engine: borderline. Some checks are near their limits.'
      };
    return {
      tone: 'decline',
      text: 'Fails rule criteria',
      detail: 'Rule engine: not eligible. One or more required checks failed.'
    };
  }

  // AI-only mode: base it on the risk band alone.
  if (engineMode === 'ai') {
    if (!riskBand || riskBand === 'UNKNOWN')
      return {
        tone: 'review',
        text: 'Risk not assessed',
        detail: 'No bank statement analysis available. Review manually.'
      };
    if (riskBand === 'LOW')
      return {
        tone: 'approve',
        text: 'Low financial risk',
        detail: 'AI analysis: low risk. Few concerning indicators in the bank statement.'
      };
    if (riskBand === 'MODERATE')
      return {
        tone: 'review',
        text: 'Moderate financial risk',
        detail: 'AI analysis: moderate risk. Review the flagged factors.'
      };
    return {
      tone: 'decline',
      text: 'High financial risk',
      detail: 'AI analysis: high risk. Multiple significant concerns found.'
    };
  }

  // Combined mode: both engines together — the strictest signal wins.
  if (!ruleVerdict && !riskBand) return null;

  const ruleFail = ruleVerdict === 'NOT_ELIGIBLE';
  const riskHigh = riskBand === 'HIGH';
  const ruleBorderline = ruleVerdict === 'BORDERLINE';
  const riskModerate = riskBand === 'MODERATE';

  if (ruleFail || riskHigh) {
    return {
      tone: 'decline',
      text: 'Suggest decline',
      detail: `Rule engine: ${ruleVerdict?.toLowerCase().replace('_', ' ') ?? 'n/a'} · AI risk: ${riskBand?.toLowerCase() ?? 'n/a'}. A significant concern was found.`
    };
  }
  if (ruleBorderline || riskModerate) {
    return {
      tone: 'review',
      text: 'Suggest careful review',
      detail: `Rule engine: ${ruleVerdict?.toLowerCase().replace('_', ' ') ?? 'n/a'} · AI risk: ${riskBand?.toLowerCase() ?? 'n/a'}. Some indicators need a closer look.`
    };
  }
  return {
    tone: 'approve',
    text: 'Suggest approve',
    detail: `Rule engine: ${ruleVerdict?.toLowerCase() ?? 'n/a'} · AI risk: ${riskBand?.toLowerCase() ?? 'n/a'}. Meets criteria across both engines.`
  };
};

const MODE_LABEL = {
  rule: 'Rule Engine',
  ai: 'AI Engine',
  combined: 'Combined (Rule + AI)'
};

const SystemRecommendation = ({ assessment, risk, engineMode }) => {
  const rec = deriveRecommendation(assessment, risk, engineMode);
  if (!rec) return null;

  return (
    <section className={`sys-rec sys-rec--${rec.tone}`}>
      <span className={`sys-rec__icon sys-rec__icon--${rec.tone}`} aria-hidden="true">
        {rec.tone === 'approve' ? '✓' : rec.tone === 'decline' ? '✕' : '!'}
      </span>

      <div className="sys-rec__body">
        <span className="sys-rec__title">System suggests: {rec.text}</span>
        <span className="sys-rec__detail">{rec.detail}</span>
        <span className="sys-rec__tag-row">
          <span className="sys-rec__tag">{MODE_LABEL[engineMode]}</span>
          <span className="sys-rec__note">
            Advisory only — the underwriter makes the final decision.
          </span>
        </span>
      </div>
    </section>
  );
};

SystemRecommendation.propTypes = {
  assessment: PropTypes.shape({ verdict: PropTypes.string }),
  risk: PropTypes.shape({ band: PropTypes.string }),
  engineMode: PropTypes.string.isRequired
};

export default SystemRecommendation;
